// /Users/donovan68/dva-frontend-vite/src/components/UseCasePreview.jsx
// Complete file with financial snapshot integration and Add to Prospect List button
import React, { useEffect, useState, useRef, useMemo } from 'react';

export default function UseCasePreview({
  useCase,
  cal,
  displayMode,   // 'Raw' | 'Calibrated' | 'Auto'
  rankLabel,     // 'quickwins' | 'strategic' | 'balanced'
  rank,          // number | null
  quadrantLabel, // string
  onClear,
  onStep,        // (dir: -1|+1) => void
  fetchDetails,  // async (id) => details | null
  onViewProfile, // (useCase) => void - for navigation
  compact = true, // New prop for compact mode
  onAddToProspect,      // NEW: Handler to add to prospect list
  isInProspectList = false  // NEW: Whether already in prospect list
}) {
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState(null);
  const [err, setErr] = useState(null);
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  
  // Cache for fetched details - persists across re-renders
  const detailsCache = useRef(new Map());
  const fetchTimeoutRef = useRef(null);

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, []);

  // Lazy-load details with caching and debouncing
  useEffect(() => {
    let cancelled = false;
    
    // Clear any pending fetch timeout
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
      fetchTimeoutRef.current = null;
    }
    
    // Reset state when use case changes or is null
    if (!useCase || !useCase.use_case_id) {
      setDetails(null);
      setErr(null);
      setLoading(false);
      return;
    }
    
    // Check if we have this in cache
    const cachedDetails = detailsCache.current.get(useCase.use_case_id);
    if (cachedDetails) {
      setDetails(cachedDetails);
      setLoading(false);
      setErr(null);
      return;
    }
    
    // If no fetchDetails function provided, skip
    if (!fetchDetails || typeof fetchDetails !== 'function') {
      setDetails(null);
      setLoading(false);
      return;
    }
    
    // Show that we're going to load (but debounced)
    setDetails(null);
    setErr(null);
    
    // Debounce the actual fetch by 500ms
    fetchTimeoutRef.current = setTimeout(async () => {
      if (cancelled) return;
      
      // Double-check we still have a valid use case ID
      if (!useCase?.use_case_id) {
        console.warn('UseCase ID became null during timeout');
        return;
      }
      
      setLoading(true);
      
      try {
        const d = await fetchDetails(useCase.use_case_id);
        
        if (!cancelled && d) {
          // Store in cache for future use
          detailsCache.current.set(useCase.use_case_id, d);
          setDetails(d);
          setErr(null);
        }
      } catch (e) {
        if (!cancelled) {
          console.error('Error fetching details:', e);
          setErr(e?.message || 'Failed to load details');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }, 500); // Wait 500ms before fetching
    
    return () => {
      cancelled = true;
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
        fetchTimeoutRef.current = null;
      }
    };
  }, [useCase?.use_case_id, fetchDetails]);

  // Prepare ROI data - prioritize financial_snapshot from view over legacy fields
  // UPDATED: Check for financial_snapshot first
  const roiData = useMemo(() => {
    if (!useCase && !details) return null;
    
    // Check if we have the new financial_snapshot from the view
    const snapshot = details?.financial_snapshot || useCase?.financial_snapshot;
    
    if (snapshot) {
      // Use the canonical financial snapshot data
      return {
        est_annual_value: snapshot.annual_revenue_potential,
        payback_months: snapshot.payback_months,
        confidence: snapshot.confidence,
        provenance: snapshot.provenance,
        revenue_band_label: snapshot.revenue_band_label,
        explanation: snapshot.explanation,
        // Add a flag to indicate this is from the new view
        isFromSnapshot: true
      };
    }
    
    // Fallback to legacy ROI data merging
    const merged = {
      ...(useCase?.roi_data || {}),
      ...(details?.roi || {}),
      isFromSnapshot: false
    };
    
    // Check if we have any ROI data
    const hasData = merged.est_annual_value || 
                   merged.payback_months || 
                   merged.roi_percentage ||
                   useCase?.roi_range ||
                   useCase?.revenue_potential;
    
    return hasData ? merged : null;
  }, [details, useCase]);

  // Empty state (AFTER all hooks)
  if (!useCase) {
    return (
      <div style={cardStyle()}>
        <div style={emptyStyle()}>
          <div style={{ fontSize: 16, marginBottom: 8, color: '#374151' }}>No use case selected</div>
          <div style={{ fontSize: 14 }}>Select a dot in the matrix or a row to preview</div>
        </div>
      </div>
    );
  }

  // Extract data with better null safety
  const title = useCase.title || useCase.name || 'Untitled Use Case';
  const category = useCase.category_name || useCase.category || '—';
  const type = useCase.type || useCase.priority_label || '—';
  const isDirectType = type === 'Direct';

  const v = cal?.value ?? useCase.value_score ?? null;
  const e = cal?.eoi ?? useCase.implementation_score ?? null;
  const t = cal?.total ?? useCase.total_score ?? null;

  // Executive summary with truncation
  const summary = details?.summary || useCase.executive_summary || useCase.description || '';
  const shouldTruncate = summary.length > 150 && !summaryExpanded;
  const displaySummary = shouldTruncate ? summary.substring(0, 150) + '...' : summary;

  return (
    <div style={cardStyle({ flex: 1 })}>
      {/* Enhanced Header */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
          <div style={{ flex: 1 }}>
            <h2 style={titleStyle()}>{title}</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
              {rank && (
                <span style={{
                  backgroundColor: '#fef3c7',
                  color: '#92400e',
                  padding: '3px 10px',
                  borderRadius: 12,
                  fontSize: 11,
                  fontWeight: 600
                }}>
                  Rank #{rank}
                </span>
              )}
              {useCase.is_baseline && (
                <span style={chipStyle('#f1f5f9', '#475569', '#cbd5e1')}>
                  Baseline
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {onClear && (
              <button
                onClick={onClear}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 6,
                  border: '1px solid #e5e7eb',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 16,
                  color: '#6b7280',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'white'}
                title="Clear selection"
              >
                ×
              </button>
            )}
          </div>
        </div>

        {/* Context line */}
        <div style={{ fontSize: 11, color: '#6b7280' }}>
          {quadrantLabel && <span>{quadrantLabel}</span>}
          {quadrantLabel && displayMode && <span> • </span>}
          {displayMode === 'Calibrated' ? 'Calibrated (35–80)' : displayMode === 'Auto' ? 'Auto-scale' : 'Raw Scores'}
        </div>
      </div>

      {/* Strategic Chips */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        <span style={chipStyle('#e5e7eb', '#111827')}>{category}</span>
        <span style={{
          ...chipStyle('#eff6ff', '#1f2937', '#dbeafe'),
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4
        }}>
          <span style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: isDirectType ? '#10b981' : '#3b82f6'
          }} />
          {type}
        </span>
        {useCase.isCustomized && <span style={chipStyle('#fef3c7', '#b45309', '#fcd34d')}>Customized</span>}
      </div>

      {/* Add to Prospect List Button - NEW */}
      {onAddToProspect && (
        <div style={{ marginBottom: 12 }}>
          {isInProspectList ? (
            <button
              disabled
              style={{
                width: '100%',
                fontSize: 13,
                fontWeight: 600,
                padding: '10px 16px',
                backgroundColor: '#f3f4f6',
                color: '#6b7280',
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                cursor: 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6
              }}
            >
              <span style={{ fontSize: 16 }}>✓</span>
              <span>Added to Prospect List</span>
            </button>
          ) : (
            <button
              onClick={() => {
                if (useCase?.use_case_id) {
                  onAddToProspect(useCase.use_case_id);
                }
              }}
              style={{
                width: '100%',
                fontSize: 13,
                fontWeight: 600,
                padding: '10px 16px',
                backgroundColor: '#FF6E4C',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#ff5533';
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(255, 110, 76, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#FF6E4C';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <span style={{ fontSize: 16 }}>+</span>
              <span>Add</span>
            </button>
          )}
        </div>
      )}

      {/* Executive Summary Section */}
      {summary && (
        <div style={{ marginBottom: 12 }}>
          <div style={sectionLabelStyle()}>
            Executive Summary
          </div>
          <p style={{ 
            margin: 0, 
            fontSize: 12, 
            color: '#374151', 
            lineHeight: 1.5 
          }}>
            {displaySummary}
          </p>
          {summary.length > 150 && (
            <button
              onClick={() => setSummaryExpanded(!summaryExpanded)}
              style={{
                marginTop: 4,
                fontSize: 11,
                color: '#64748b',
                textDecoration: 'underline',
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer'
              }}
            >
              {summaryExpanded ? 'Show less' : 'Show more'}
            </button>
          )}
        </div>
      )}

      {/* ROI Highlight Card - UPDATED with financial snapshot support */}
      {(roiData || loading) && !err && (
        <div style={{
          borderRadius: 10,
          border: '1px solid #fed7aa',
          backgroundColor: '#fff7ed',
          padding: 12,
          marginBottom: 12
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            marginBottom: 6
          }}>
            <div style={sectionLabelStyle({ color: '#92400e' })}>
              Financial Snapshot
            </div>
            {/* Show baseline indicator if using fallback data */}
            {roiData?.provenance === 'Baseline category band' && (
              <span style={{
                fontSize: 9,
                padding: '2px 6px',
                borderRadius: 4,
                backgroundColor: '#fed7aa',
                color: '#92400e',
                fontWeight: 600
              }}>
                BASELINE
              </span>
            )}
          </div>
          
          {loading && !roiData ? (
            // Loading skeleton
            <div style={{ display: 'flex', gap: 12, opacity: 0.5 }}>
              <div style={{ flex: 1 }}>
                <div style={{ height: 8, backgroundColor: '#fed7aa', borderRadius: 4, marginBottom: 4 }} />
                <div style={{ height: 12, backgroundColor: '#fed7aa', borderRadius: 4, width: '70%' }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ height: 8, backgroundColor: '#fed7aa', borderRadius: 4, marginBottom: 4 }} />
                <div style={{ height: 12, backgroundColor: '#fed7aa', borderRadius: 4, width: '50%' }} />
              </div>
            </div>
          ) : roiData ? (
            // Actual data display
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: (roiData.est_annual_value || useCase.revenue_potential) && (roiData.payback_months || useCase.time_to_roi) ? '1fr 1fr' : '1fr', gap: 12 }}>
                {/* Revenue/Value */}
                {(roiData.est_annual_value || useCase.revenue_potential) && (
                  <div>
                    <div style={{ fontSize: 10, color: '#92400e', marginBottom: 2 }}>
                      Annual Revenue Potential
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
                      {roiData.est_annual_value ? 
                        `$${formatNumber(Math.round(roiData.est_annual_value))}/yr` :
                        useCase.revenue_potential
                      }
                    </div>
                  </div>
                )}
                
                {/* Payback Period */}
                {(roiData.payback_months || useCase.time_to_roi) && (
                  <div>
                    <div style={{ fontSize: 10, color: '#92400e', marginBottom: 2 }}>
                      Payback Period
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
                      {roiData.payback_months ? 
                        `${roiData.payback_months} months` :
                        useCase.time_to_roi
                      }
                    </div>
                  </div>
                )}
              </div>
              
              {/* ROI Percentage if available */}
              {(roiData.roi_percentage || useCase.roi_range) && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 10, color: '#92400e', marginBottom: 2 }}>
                    Return on Investment
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#059669' }}>
                    {roiData.roi_percentage ? 
                      `${roiData.roi_percentage}%` :
                      useCase.roi_range
                    }
                  </div>
                </div>
              )}
              
              {/* Confidence Level with color coding */}
              {roiData.confidence && (
                <div style={{ 
                  marginTop: 8,
                  paddingTop: 8,
                  borderTop: '1px solid #fed7aa',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <div>
                    <span style={{ fontSize: 10, color: '#92400e' }}>Confidence: </span>
                    <span style={{ 
                      fontSize: 11, 
                      fontWeight: 600,
                      color: roiData.confidence === 'High' ? '#059669' : 
                             roiData.confidence === 'Low' ? '#dc2626' : '#d97706'
                    }}>
                      {roiData.confidence}
                    </span>
                  </div>
                  
                  {/* Show info icon with explanation on hover */}
                  {roiData.explanation && (
                    <div 
                      title={roiData.explanation}
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        backgroundColor: '#fed7aa',
                        color: '#92400e',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 10,
                        fontWeight: 600,
                        cursor: 'help'
                      }}
                    >
                      ?
                    </div>
                  )}
                </div>
              )}
              
              {/* Revenue band label for context */}
              {roiData.revenue_band_label && (
                <div style={{
                  marginTop: 6,
                  fontSize: 9,
                  color: '#92400e',
                  opacity: 0.8
                }}>
                  Category range: {roiData.revenue_band_label}
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}

      {/* Score Breakdown - Enhanced */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          marginBottom: 8
        }}>
          <div style={sectionLabelStyle()}>
            Score Breakdown
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <span style={scoreChipStyle('V', formatScore(v), false, '#3b82f6')}>
              <span style={{ fontSize: 9, opacity: 0.8 }}>V</span>
              <span style={{ fontWeight: 600 }}>{formatScore(v)}</span>
            </span>
            <span style={scoreChipStyle('E', formatScore(e), false, '#10b981')}>
              <span style={{ fontSize: 9, opacity: 0.8 }}>E</span>
              <span style={{ fontWeight: 600 }}>{formatScore(e)}</span>
            </span>
            <span style={scoreChipStyle('T', formatScore(t), true, '#FF6E4C')}>
              <span style={{ fontSize: 9, opacity: 0.8 }}>T</span>
              <span style={{ fontWeight: 700 }}>{formatScore(t)}</span>
            </span>
          </div>
        </div>

        {/* Mini score bars */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <MiniBar label="Strategic Value" value={v} max={100} color="#3b82f6" />
          <MiniBar label="Implementation" value={e} max={100} color="#10b981" />
        </div>
      </div>

      {/* Additional Details when loaded */}
      {!loading && !err && details && (
        <>
          {(details.effort_tier || details.time_to_pilot_weeks || details.dependencies?.length > 0) && (
            <Section title="Implementation">
              <ul style={listStyle()}>
                {details.effort_tier && <li>Effort: <strong>{details.effort_tier}</strong></li>}
                {details.time_to_pilot_weeks && <li>Time-to-Pilot: <strong>{details.time_to_pilot_weeks} weeks</strong></li>}
                {details.dependencies?.length > 0 && (
                  <li>Key Dependencies: {details.dependencies.slice(0, 2).join(', ')}</li>
                )}
              </ul>
            </Section>
          )}

          {(details.readiness_score != null || details.data_sources?.length > 0) && (
            <Section title="Readiness">
              <ul style={listStyle()}>
                {details.readiness_score != null && (
                  <li>Score: <strong>{details.readiness_score}/100</strong></li>
                )}
                {details.data_sources?.length > 0 && (
                  <li>Data Sources: {details.data_sources.slice(0, 3).join(', ')}</li>
                )}
              </ul>
            </Section>
          )}
        </>
      )}

      {/* Error state */}
      {err && <ErrorMessage msg={err} />}

      {/* Action Buttons - Enhanced */}
      <div style={{ 
        display: 'flex', 
        gap: 8, 
        marginTop: 16,
        paddingTop: 12,
        borderTop: '1px solid #f3f4f6'
      }}>
        {onStep && (
          <>
            <button
              onClick={() => onStep(-1)}
              style={secondaryButtonStyle()}
              title="Previous use case"
            >
              ← Prev
            </button>
            <button
              onClick={() => onStep(1)}
              style={secondaryButtonStyle()}
              title="Next use case"
            >
              Next →
            </button>
          </>
        )}
        {onViewProfile && (
          <button
            onClick={() => {
              if (useCase && useCase.use_case_id) {
                onViewProfile(useCase);
              }
            }}
            style={primaryButtonStyle()}
          >
            View Full Profile →
          </button>
        )}
      </div>
    </div>
  );
}

/* ---------- Helper Components ---------- */
function MiniBar({ label, value, max, color }) {
  const percentage = value != null ? Math.min(100, (value / max) * 100) : 0;
  
  return (
    <div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 3
      }}>
        <span style={{ fontSize: 10, color: '#64748b' }}>{label}</span>
        <span style={{ fontSize: 10, color: '#374151', fontWeight: 500 }}>
          {value != null ? `${Math.round(value)}/${max}` : '—'}
        </span>
      </div>
      <div style={{
        width: '100%',
        height: 4,
        backgroundColor: '#f1f5f9',
        borderRadius: 2,
        overflow: 'hidden'
      }}>
        <div
          style={{
            width: `${percentage}%`,
            height: '100%',
            backgroundColor: color,
            transition: 'width 0.3s ease'
          }}
        />
      </div>
    </div>
  );
}

function Section({ title, children }) { 
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={sectionLabelStyle()}>
        {title}
      </div>
      {children}
    </div>
  ); 
}

function ErrorMessage({ msg }) { 
  return (
    <div style={{ 
      fontSize: 11, 
      color: '#b91c1c', 
      background: '#fee2e2', 
      border: '1px solid #fecaca', 
      borderRadius: 6, 
      padding: '6px 8px' 
    }}>
      {msg}
    </div>
  ); 
}

/* ---------- Style functions ---------- */
function cardStyle(extra = {}) { 
  return { 
    background: 'white', 
    borderRadius: 12, 
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)', 
    padding: 20, 
    display: 'flex', 
    flexDirection: 'column', 
    minHeight: 0, 
    ...extra 
  }; 
}

function titleStyle() { 
  return { 
    fontSize: 18, 
    fontWeight: 600, 
    margin: 0,
    color: '#18365E',
    lineHeight: 1.3
  }; 
}

function chipStyle(bg = '#e5e7eb', color = '#111827', borderColor = null) { 
  return { 
    fontSize: 10, 
    padding: '3px 8px', 
    borderRadius: 6, 
    background: bg, 
    color, 
    border: borderColor ? `1px solid ${borderColor}` : 'none', 
    fontWeight: 500 
  }; 
}

function scoreChipStyle(label, val, primary, color) { 
  return { 
    display: 'inline-flex', 
    alignItems: 'center', 
    gap: 3, 
    padding: '3px 8px', 
    borderRadius: 6, 
    backgroundColor: primary ? color : `${color}15`,
    color: primary ? 'white' : color,
    border: `1px solid ${primary ? color : `${color}30`}`,
    fontSize: 11, 
    fontWeight: primary ? 700 : 600,
    ...(primary && {
      boxShadow: `0 0 0 2px ${color}15`
    })
  }; 
}

function primaryButtonStyle() { 
  return { 
    flex: 1,
    fontSize: 12, 
    fontWeight: 600, 
    padding: '8px 12px', 
    backgroundColor: '#FF6E4C', 
    color: 'white', 
    border: 'none', 
    borderRadius: 8, 
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  }; 
}

function secondaryButtonStyle() {
  return {
    fontSize: 12,
    fontWeight: 500,
    padding: '8px 12px',
    backgroundColor: 'white',
    color: '#374151',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'all 0.2s'
  };
}

function emptyStyle() { 
  return { 
    padding: 40, 
    textAlign: 'center',
    color: '#6b7280', 
    fontSize: 12 
  }; 
}

function listStyle() { 
  return { 
    margin: 0, 
    paddingLeft: 18, 
    fontSize: 11, 
    color: '#374151',
    lineHeight: 1.6
  }; 
}

function sectionLabelStyle(extra = {}) {
  return {
    fontSize: 11,
    fontWeight: 600,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: 6,
    ...extra
  };
}

/* ---------- Utility functions ---------- */
function formatScore(n) { 
  return (n == null || Number.isNaN(n)) ? '—' : Number(n).toFixed(0); 
}

function formatNumber(n) { 
  if (!n) return '0';
  try { 
    return Number(n).toLocaleString(); 
  } catch { 
    return String(n); 
  } 
}